interface PrintOptions {
  title?: string
  isRTL?: boolean
  styles?: string
  headTags?: string
}

export const printPageNumberCss = `
@page {
  margin-bottom: 16mm;
  @bottom-center {
    content: "صفحة " counter(page) " من " counter(pages);
    font-family: Tahoma, "Segoe UI", Arial, sans-serif;
    font-size: 11pt;
    color: #111111;
  }
}
@media print {
  html, body {
    background: #fff !important;
  }
  /* A tall white sheet painted after the margin box hides the page number. */
  .print-container,
  .print-content,
  .invoice-sheet,
  .pro-invoice,
  .sheet,
  .a4-document,
  .print-page {
    background: transparent !important;
    background-color: transparent !important;
    box-shadow: none !important;
  }
}
`.trim()

/** Page numbers on every browser print, including documents opened in a new window. */
export function installPrintPageNumbers() {
  const apply = () => {
    const parent = document.head || document.documentElement
    let el = document.getElementById('print-page-numbers') as HTMLStyleElement | null
    if (!el) {
      el = document.createElement('style')
      el.id = 'print-page-numbers'
    }
    el.textContent = printPageNumberCss
    parent.appendChild(el)
  }

  const pinLast = () => {
    apply()
    const el = document.getElementById('print-page-numbers')
    if (el && document.body) document.body.appendChild(el)
  }

  if (document.head) apply()
  else document.addEventListener('DOMContentLoaded', apply)
  window.addEventListener('beforeprint', pinLast)

  const originalWrite = Document.prototype.write
  Document.prototype.write = function (this: Document, ...args: string[]) {
    const html = args.map((part) => String(part)).join('')
    if (this !== document && !html.includes('id="print-page-numbers"') && !html.includes('data-thermal-80')) {
      const block = `<style id="print-page-numbers">${printPageNumberCss}</style>`
      const injected = /<\/body>/i.test(html)
        ? html.replace(/<\/body>/i, `${block}</body>`)
        : html.replace(/<\/head>/i, `${block}</head>`)
      return originalWrite.call(this, injected)
    }
    return originalWrite.call(this, html)
  }
}

const defaultStyles = `
  body { font-family: Arial, sans-serif; margin: 24px; color: #111827; }
  h1, h2, h3 { margin: 0 0 12px; }
  table { width: 100%; border-collapse: collapse; margin-top: 12px; }
  th, td { border: 1px solid #e5e7eb; padding: 8px; font-size: 12px; }
  th { background: #f3f4f6; text-align: start; }
  .print-header { margin-bottom: 16px; }
  .print-muted { color: #6b7280; font-size: 12px; }
`

export function openPrintWindow(html: string, options: PrintOptions = {}) {
  const { title, isRTL, styles, headTags } = options
  const printWindow = window.open('', '_blank')
  if (!printWindow) return

  const documentTitle = title ? `${title} - Print` : 'Print'
  const baseUrl = window.location.origin + '/'

  const content = `
    <!doctype html>
    <html lang="${isRTL ? 'ar' : 'en'}" dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8" />
        <base href="${baseUrl}" />
        <title>${documentTitle}</title>
        <style>${defaultStyles}</style>
        ${headTags || ''}
        ${styles ? `<style>${styles}</style>` : ''}
        <style>
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${html}
        <style id="print-page-numbers">${printPageNumberCss}</style>
        <script>
          function printWhenReady() {
            // Wait for fonts
            document.fonts.ready.then(() => {
              // Wait for images
              const images = Array.from(document.getElementsByTagName('img'));
              const promises = images.map(img => {
                if (img.complete) return Promise.resolve();
                return new Promise(resolve => {
                  img.onload = resolve;
                  img.onerror = resolve; // Don't block on error
                });
              });

              Promise.all(promises).then(() => {
                setTimeout(() => {
                  window.print();
                }, 500);
              });
            });
          }

          if (document.readyState === 'complete') {
            printWhenReady();
          } else {
            window.onload = printWhenReady;
          }
        </script>
      </body>
    </html>
  `

  printWindow.document.write(content)
  printWindow.document.close()
  printWindow.focus()
}

export function printElement(element: HTMLElement, options: PrintOptions = {}) {
  const appStyles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
    .map(style => {
      // Clone the node to avoid modifying the original
      const clone = style.cloneNode(true) as HTMLElement
      // If it's a link, ensure href is absolute
      if (clone.tagName.toLowerCase() === 'link' && (clone as HTMLLinkElement).href) {
        (clone as HTMLLinkElement).href = (clone as HTMLLinkElement).href
      }
      return clone.outerHTML
    })
    .join('\n')
    
  const printColorStyles = `
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    body {
      background-color: white !important;
      color: black !important;
    }
  `

  // Deep clone to modify attributes without affecting UI
  const clone = element.cloneNode(true) as HTMLElement
  
  // Convert images to absolute URLs and eager loading
  const images = clone.getElementsByTagName('img')
  for (let i = 0; i < images.length; i++) {
    const currentSrc = images[i].getAttribute('src')
    if (currentSrc) {
      try {
        const absolute = new URL(currentSrc, window.location.href).href
        images[i].src = absolute
      } catch {
        // keep original src if URL construction fails
      }
    }
    images[i].loading = 'eager'
  }

  const html = clone.outerHTML

  // Pass appStyles as headTags since they contain <style> and <link> tags
  // Pass printColorStyles and options.styles as raw CSS
  openPrintWindow(html, { 
    ...options, 
    headTags: appStyles,
    styles: `${printColorStyles}\n${options.styles || ''}` 
  })
}

type PageNumberPdf = {
  getNumberOfPages: () => number
  setPage: (page: number) => void
  setFontSize: (size: number) => void
  setTextColor: (red: number, green: number, blue: number) => void
  setFillColor: (red: number, green: number, blue: number) => void
  rect: (x: number, y: number, w: number, h: number, style?: string) => void
  text: (text: string, x: number, y: number, options?: { align?: 'center' }) => void
  internal: { pageSize: { getWidth: () => number; getHeight: () => number } }
}

/** Page numbers on image PDFs, which ignore CSS margin boxes. */
export function stampPdfPageNumbers(pdf: PageNumberPdf) {
  const total = pdf.getNumberOfPages()
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i)
    const width = pdf.internal.pageSize.getWidth()
    const height = pdf.internal.pageSize.getHeight()
    pdf.setFillColor(255, 255, 255)
    pdf.rect(0, height - 8, width, 8, 'F')
    pdf.setFontSize(10)
    pdf.setTextColor(17, 17, 17)
    pdf.text(`${i} / ${total}`, width / 2, height - 3, { align: 'center' })
  }
}
